// SQL + 바인드변수 텍스트 파서
//
// 지원 포맷:
// 1. 주석 기반: -- varname = value (별도 섹션 또는 SQL 앞뒤)
// 2. 인라인 주석: :varname /* value */
// 3. 변수만 감지: SQL에 :varname 있으나 값 없음
//
// 쿼리 구분: 세미콜론(;)으로 분리 (문자열 리터럴 내부 세미콜론 제외)

import i18n from '../i18n';
// 바인드 검출은 편집기와 **같은 구현**을 쓴다 — 두 벌이면 편집기가 묻는 바인드와
// 튜닝 패널이 세는 바인드가 갈린다(`sqlBinds.js` 머리말의 경고가 그것이다).
import { detectBindVariables } from './sqlBinds';
const MAX_STATEMENTS = 5;

/**
 * 편집기 파일에 저장된 바인드 값(`{ statement_0: { name: value } }`)을 이름 하나에 값 하나로 편다.
 * 앞 문장의 값이 먼저다 — 편집기 실행(`handleExecute`)이 값을 찾는 순서와 같다.
 * @returns {Object<string, string>}
 */
export function flattenEditorBinds(fileBindVariables) {
  const out = {};
  for (const [key, vars] of Object.entries(fileBindVariables || {})) {
    if (!key.startsWith('statement_') || !vars || typeof vars !== 'object') continue;
    for (const [name, raw] of Object.entries(vars)) {
      if (name in out) continue;
      const v = raw && typeof raw === 'object' ? raw.value : raw;
      if (v === undefined || v === null) continue;
      out[name] = String(v);
    }
  }
  return out;
}

/**
 * 텍스트에서 여러 SQL과 바인드변수를 파싱 (다중 쿼리 지원)
 *
 * 값의 출처는 둘이다 — 편집기의 바인드 변수 설정(`opts.editorBinds`, 편집기에서 실행할 때 입력해
 * 둔 값)과 텍스트의 `-- 변수 = 값` 줄. 텍스트 쪽이 더 가까운 지정이라 이긴다. 예전에는 편집기에
 * 입력해 둔 값을 **전혀 가져오지 않아** 가져오기 할 때마다 바인드가 빈 칸이었다(신고 2026-10-07).
 *
 * @param {string} text
 * @param {{ editorBinds?: Object<string,string>, editorBindTypes?: Object<string,string> }} [opts]
 * @returns {{ statements: Array<{ sql, bindVariables, parseWarnings }> }}
 */
export function parseMultiSQLWithBindVariables(text, opts = {}) {
  if (!text || typeof text !== 'string' || !text.trim()) {
    return { statements: [{ sql: '', bindVariables: {}, parseWarnings: [i18n.t('common:tp.emptyText')] }] };
  }

  // 먼저 바인드변수 섹션을 전체에서 추출
  const globalBindVars = {};
  // 형식은 추론하지 않는다(2026-10-26) — 편집기에서 사람이 고른 형식(editorBindTypes)만, 없으면 VARCHAR2.
  // 실행(편집기)도 글자로 묶으므로, 여기서 숫자로 바꾸면 실행과 실행계획이 다른 바인드로 떠 계획이 갈린다
  const types = opts.editorBindTypes || {};
  for (const [name, value] of Object.entries(opts.editorBinds || {})) {
    globalBindVars[name] = { value, type: types[name] === 'NUMBER' ? 'NUMBER' : 'VARCHAR2' };
  }
  let workingText = text.trim();

  // "-- Bind Variables:" 섹션 추출
  const bindSectionRegex = /^--\s*bind\s*variables?\s*:?\s*$/im;
  const bindSectionMatch = workingText.match(bindSectionRegex);
  if (bindSectionMatch) {
    const sectionIdx = workingText.indexOf(bindSectionMatch[0]);
    const bindPart = workingText.substring(sectionIdx + bindSectionMatch[0].length).trim();
    workingText = workingText.substring(0, sectionIdx).trim();
    parseBindCommentLines(bindPart, globalBindVars);
  } else {
    // 개별 주석 라인에서 바인드변수 추출
    const lines = workingText.split('\n');
    const sqlLines = [];
    for (const line of lines) {
      const bindMatch = line.match(/^\s*--\s*(\w+)\s*[:=]\s*(.+?)\s*$/);
      if (bindMatch && !line.match(/^\s*--\s*(SELECT|FROM|WHERE|AND|OR|JOIN|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER)/i)) {
        parseBindCommentLines(line, globalBindVars);
      } else {
        sqlLines.push(line);
      }
    }
    workingText = sqlLines.join('\n').trim();
  }

  // 세미콜론으로 쿼리 분리 (문자열 리터럴 내부 세미콜론 무시)
  const rawStatements = splitStatements(workingText);

  // 각 쿼리별 파싱
  const statements = [];
  for (let i = 0; i < Math.min(rawStatements.length, MAX_STATEMENTS); i++) {
    const stmtText = rawStatements[i].trim();
    if (!stmtText) continue;

    const result = parseSingleStatement(stmtText, globalBindVars);
    statements.push(result);
  }

  if (statements.length === 0) {
    return { statements: [{ sql: '', bindVariables: {}, parseWarnings: [i18n.t('common:tp.noParsableSql')] }] };
  }

  if (rawStatements.length > MAX_STATEMENTS) {
    statements[0].parseWarnings.push(i18n.t('common:tp.tooManyStatements', { max: MAX_STATEMENTS, found: rawStatements.length }));
  }

  return { statements };
}

/**
 * 단일 SQL 문장 파싱
 */
function parseSingleStatement(sqlText, globalBindVars) {
  const parseWarnings = [];
  let sql = sqlText;
  const bindVariables = {};

  // **`:var /* ... */` 의 주석을 값으로 읽지 않는다** (사용자 결정 2026-09-18).
  //
  // 예전에는 인라인 주석을 그 바인드의 값으로 삼았다. 그런데 `/* */` 에는 값이 아니라 **설명**이
  // 들어 있는 경우가 훨씬 많다 — 편집기에서 지정해 둔 값 대신 주석 글이 값으로 들어가
  // "설정한 값이 아닌 엉뚱한 값이 뜬다" 는 신고가 왔다. 주석은 주석으로 둔다.
  //
  // 값을 주는 길은 **둘 다 명시적**이다: 편집기의 바인드 변수 설정(`globalBindVars`), 또는
  // `-- var = value` 주석 줄. 추측하지 않는다.
  //
  // SQL 자체에서는 주석을 걷어낸다 — 주석 안의 `:word` 가 바인드로 잡히면 안 되기 때문이다.

  // 바인드 감지는 **편집기와 같은 것을 본다**(`sqlBinds.detectBindVariables`).
  //
  // 예전에는 여기 자체 정규식(`/[:&]([a-zA-Z_][a-zA-Z0-9_]*)/`)이 따로 있었고, 문자열은
  // `'[^']*'` 로 지웠다. 그래서 놓치는 것이 많았다 — 위치 바인드 `:1`, `''` 로 이스케이프된
  // 따옴표가 든 문자열 뒤의 바인드, 주석 안의 가짜 바인드. "10개 중 6개만 뜬다" 는 신고가
  // 그것이고, 빠진 채로 실행계획을 부르면 **ORA-01008(변수가 다 묶이지 않음)** 로 죽는다.
  // 편집기가 세는 것과 여기가 세는 것이 다르면 안 된다 — 그래서 한 곳만 쓴다.
  //
  // **이 문장에 실제로 있는 바인드만** 담는다. 예전에는 전체 값 목록을 문장마다 통째로 복사해서,
  // 다른 문장의 변수까지 묶여 Oracle 이 ORA-01036(잘못된 변수 이름)으로 거절할 수 있었다.
  //
  // 「값을 입력해 주세요」 경고는 여기서 만들지 않는다 — 가져올 때 한 번 정해 두면 값을 넣은 뒤에도
  // 그대로 남아 있었다(신고 2026-10-07). 빈 값 안내는 패널이 지금 값을 보고 그때그때 낸다.
  const detectedVarNames = detectBindVariables(sql).filter(name => !isOracleKeyword(name));
  for (const varName of detectedVarNames) {
    bindVariables[varName] = globalBindVars[varName] || { value: '', type: 'VARCHAR2' };
  }

  sql = sql.replace(/;\s*$/, '').trim();

  return { sql, bindVariables, parseWarnings };
}

/**
 * 세미콜론으로 SQL 문장 분리 — 문자열·**주석** 안의 세미콜론·따옴표는 무시한다.
 *
 * 예전에는 주석을 몰라서, 머리 블록 주석 안에 세미콜론이나 따옴표(it's)가 있으면 그 자리에서 문장이 잘려
 * 「쿼리 1 = 주석 시작 줄, 쿼리 2 = 주석 나머지 + SELECT」 처럼 가져왔다(신고 2026-10-26). 주석만 있는 조각은 다음 문장에 붙인다.
 */
function splitStatements(text) {
  const chunks = [];
  let current = '';
  let i = 0;
  const n = text.length;
  while (i < n) {
    const ch = text[i];
    const nx = text[i + 1];
    if (ch === '-' && nx === '-') {                       // 줄 주석 — 줄 끝까지 그대로
      const end = text.indexOf('\n', i);
      const stop = end < 0 ? n : end;
      current += text.slice(i, stop);
      i = stop;
      continue;
    }
    if (ch === '/' && nx === '*') {                       // 블록 주석 — */ 까지 그대로
      const end = text.indexOf('*/', i + 2);
      const stop = end < 0 ? n : end + 2;
      current += text.slice(i, stop);
      i = stop;
      continue;
    }
    // 문자열·따옴표 이름 — 닫힐 때까지('' 는 이스케이프)
    if (ch === "'" || ch === '"') {
      let j = i + 1;
      while (j < n) {
        if (text[j] === ch) {
          if (ch === "'" && text[j + 1] === "'") { j += 2; continue; }
          break;
        }
        j += 1;
      }
      const stop = Math.min(n, j + 1);
      current += text.slice(i, stop);
      i = stop;
      continue;
    }
    if (ch === ';') {
      chunks.push(current);
      current = '';
      i += 1;
      continue;
    }
    current += ch;
    i += 1;
  }
  chunks.push(current);

  // 주석·빈칸뿐인 조각은 문장이 아니다 — 다음 문장 앞에 붙인다(마지막이면 버린다)
  const onlyComments = (t) => !t.replace(/--[^\n]*/g, '').replace(/\/\*[\s\S]*?(\*\/|$)/g, '').trim();
  const statements = [];
  let carry = '';
  for (const c of chunks) {
    if (onlyComments(c)) { if (c.trim()) carry += `${c.trim()}\n`; continue; }
    statements.push(`${carry}${c}`.trim());
    carry = '';
  }
  return statements;
}

/**
 * 기존 호환용: 단일 SQL 파싱 (App.js handleTuningParseFromEditor에서 사용)
 */
export function parseSQLWithBindVariables(text) {
  const result = parseMultiSQLWithBindVariables(text);
  if (result.statements.length === 1) {
    return result.statements[0];
  }
  // 다중 쿼리인 경우 첫 번째만 반환 (레거시 호환)
  return result.statements[0];
}

function parseBindCommentLines(text, bindVariables) {
  const lines = text.split('\n');
  for (const line of lines) {
    const match = line.match(/^\s*--?\s*(\w+)\s*[:=]\s*(.+?)\s*$/);
    if (match) {
      const varName = match[1];
      const rawValue = match[2].trim();
      bindVariables[varName] = parseBindValue(rawValue);
    }
  }
}

function parseBindValue(rawValue) {
  if ((rawValue.startsWith("'") && rawValue.endsWith("'")) ||
      (rawValue.startsWith('"') && rawValue.endsWith('"'))) {
    return { value: rawValue.slice(1, -1), type: 'VARCHAR2' };
  }
  if (/^-?\d+(\.\d+)?$/.test(rawValue)) {
    return { value: rawValue, type: 'NUMBER' };
  }
  if (/^\d{4}[-/]\d{2}[-/]\d{2}/.test(rawValue)) {
    return { value: rawValue, type: 'DATE' };
  }
  if (rawValue.toUpperCase() === 'NULL') {
    return { value: null, type: 'VARCHAR2' };
  }
  return { value: rawValue, type: 'VARCHAR2' };
}

function isOracleKeyword(name) {
  const keywords = new Set([
    'ROWNUM', 'ROWID', 'LEVEL', 'SYSDATE', 'SYSTIMESTAMP',
    'USER', 'UID', 'NULL', 'TRUE', 'FALSE'
  ]);
  return keywords.has(name.toUpperCase());
}

export default parseSQLWithBindVariables;
